import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { IGroupEntity } from '../../../../models'
import GroupsApiService from '../../../../services/groupsApiService'
import { fetchGroupList, updateGroupsData } from '../../../../store/groupsSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { notifyUser } from '../../../../store/notificationsSlice'
import { fetchRations } from '../../../../store/rationSystemSlice'

type UseGroupEditProps = {
	groupId?: number
	isFirstTry?: boolean
	onClose?: () => void
}

export const useGroupEdit = ({
	groupId,
	isFirstTry,
	onClose,
}: UseGroupEditProps) => {
	const urlParams = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const dispatch = useAppDispatch()

	const { groupList } = useAppSelector(store => store.groupsReducer)
	const { rationArray } = useAppSelector(store => store.rationSystemReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const { permissions } = useAppSelector(state => state.authReducer)

	const [groupData, setGroupData] = useState<IGroupEntity | undefined>()
	const [initialGroupData, setInitialGroupData] = useState<
		IGroupEntity | undefined
	>()
	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		if (!groupList || groupList.length === 0) dispatch(fetchGroupList())
		if (!rationArray || rationArray.length === 0) dispatch(fetchRations())
	}, [dispatch])

	useEffect(() => {
		const selectedGroup = !isFirstTry
			? groupList.find(group => group.key.toString() === urlParams.groupkey)
			: groupList.find(group => group.id === groupId)

		setGroupData(selectedGroup)
		setInitialGroupData(selectedGroup)
	}, [groupList, groupId, isFirstTry, urlParams.groupkey])

	const setObjectValue = useCallback(
		(field: keyof IGroupEntity, value: any) => {
			if (!groupData) return
			setGroupData({ ...groupData, [field]: value })
		},
		[groupData],
	)

	const navigateToGroupPage = useCallback(
		(groupKey: string | number) => {
			const targetPath = `/feed/groups/${groupKey}`
			if (location.pathname === targetPath) return

			navigate(`${targetPath}${location.search}`)
		},
		[location.pathname, location.search, navigate],
	)

	const saveChangesAtServer = useCallback(async () => {
		if (!groupData || isSaving) return

		const data = {
			id: groupData.key,
			title: groupData.title,
			description: groupData.description,
			cattles_qty: groupData.cattles_qty,
			cattles_limit: groupData.cattles_limit,
			weight_per_head: groupData.weight_per_head,
			ration_id: groupData.ration || null,
			rationtitle: groupData.rationtitle || null,
			barn: groupData.barn,
			status: groupData.status,
		}

		setIsSaving(true)
		try {
			const response = await GroupsApiService.putGroupsDataKey(data)
			if (response.status === 200) {
				dispatch(
					notifyUser({
						title: 'Данные успешно сохранены',
						description: '',
						isError: false,
					}),
				)
				dispatch(updateGroupsData(response.data))
				if (!isFirstTry) navigateToGroupPage(groupData.key)
				onClose && onClose()
			}
		} catch {
			dispatch(
				notifyUser({
					title: 'Произошла ошибка',
					description: 'Попробуйте снова.',
					isError: true,
				}),
			)
		} finally {
			setIsSaving(false)
		}
	}, [groupData, isSaving, dispatch, navigateToGroupPage, isFirstTry, onClose])

	const cancelEdit = useCallback(() => {
		setGroupData(initialGroupData)
		if (groupData) navigateToGroupPage(groupData.key)
	}, [groupData, initialGroupData, navigateToGroupPage])

	return {
		groupData,
		initialGroupData,
		setObjectValue,
		saveChangesAtServer,
		isSaving,
		cancelEdit,
		userData,
		permissions,
		rationArray,
	}
}
